import { Briefcase, ChevronDown } from "lucide-react";
import React, { useState } from "react";

import JobListingItem from "../../../../../JobsPage/components/JobListingItem";
import { JobDetails } from "../utils/helpers";

interface MoreJobsProps {
	moreJobs: JobDetails[];
	currentJobId?: string;
	onJobSelect: (job: JobDetails) => void;
	onJobClick: (job: JobDetails) => void;
}

export function MoreJobs({ moreJobs, currentJobId, onJobSelect, onJobClick }: MoreJobsProps) {
	// Collapsed state for mobile - default to collapsed on mobile
	const [isExpanded, setIsExpanded] = useState(false);

	// Check if we're on mobile
	const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

	const filteredJobs = moreJobs.filter((item) => item?._id !== currentJobId);

	if (filteredJobs.length === 0) {
		return null;
	}

	return (
		<div className="mt-12">
			{/* Section Header */}
			<div
				className="flex items-center justify-between cursor-pointer md:cursor-default py-3 border-b border-gray-200 mb-4"
				onClick={() => isMobile && setIsExpanded(!isExpanded)}
			>
				<div className="flex items-center gap-2">
					<Briefcase className="h-5 w-5 text-gray-500" />
					<div>
						<h2 className="text-lg font-semibold font-plus-jakarta text-gray-900">More Job Opportunities</h2>
						<p className="text-xs text-gray-500 hidden sm:block">
							Explore {filteredJobs.length} more openings that might interest you
						</p>
					</div>
				</div>

				{/* Mobile expand/collapse button */}
				<button
					className="md:hidden p-1 rounded-full hover:bg-gray-100 transition-colors"
					aria-expanded={isExpanded}
					aria-label={isExpanded ? "Collapse job listings" : "Expand job listings"}
				>
					<ChevronDown
						className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
					/>
				</button>
			</div>

			{/* Mobile hint */}
			<p className="text-xs text-gray-500 mb-3 sm:hidden">
				{isExpanded ? `Showing ${filteredJobs.length} jobs` : "Tap to view more job opportunities"}
			</p>

			{/* Job Listings - Always visible on desktop, collapsible on mobile */}
			<div
				className={`${
					isMobile && !isExpanded ? "hidden" : "block md:block" // Always show on desktop
				}`}
			>
				{filteredJobs.map((item, index) => {
					return (
						<JobListingItem
							key={index}
							job={{
								...item,
								logo: item.company?.logo_url ? item.company.logo_url : "/images/co-placeholder.png",
							}}
							onApply={() => onJobSelect(item)}
							onClick={() => onJobClick(item)}
							showCompany={true}
							titleClassName="text-base"
						/>
					);
				})}
			</div>

			{/* Mobile expand CTA when collapsed */}
			{isMobile && !isExpanded && (
				<button
					onClick={() => setIsExpanded(true)}
					className="mt-3 w-full py-2 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-600 font-medium transition-colors flex items-center justify-center gap-2"
				>
					<span>View {filteredJobs.length} more jobs</span>
					<ChevronDown className="h-4 w-4" />
				</button>
			)}
		</div>
	);
}
